/**
 * @file useReviewModeration.ts
 * @description Хук с действиями модерации одного отзыва (phase19.10):
 * смена статуса, ответ магазина, обезличивание, подгрузка истории.
 *
 * Держит только «серверную» часть: флаг busy, тосты и вызовы API. Состояние
 * раскрытых панелей остаётся в ProductReviewActions. Правки текста отзыва
 * среди действий нет — R3 docs/business/reviews.md.
 */

import { useState } from "react";

import { useNotify } from "../../../components/ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import {
  productReviews,
  type ProductReviewStatus,
  type ReviewHistoryEntry,
} from "../../../lib/api-product-reviews";

export function useReviewModeration(reviewId: string, onChanged: () => void) {
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<ReviewHistoryEntry[] | null>(null);

  function fail(err: unknown, fallback: string) {
    notify.error(err instanceof ApiError ? err.message : fallback);
  }

  /** reason обязателен при REJECTED — без него бэк вернёт 400 (R3). */
  async function changeStatus(
    status: ProductReviewStatus,
    reason?: string,
  ): Promise<boolean> {
    setBusy(true);
    try {
      await productReviews.setStatus(reviewId, status, reason);
      notify.success(status === "PUBLISHED" ? "Отзыв опубликован" : "Статус изменён");
      onChanged();
      return true;
    } catch (err) {
      fail(err, "Не удалось изменить статус");
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** Пустая строка удаляет ответ магазина. */
  async function saveReply(reply: string): Promise<boolean> {
    setBusy(true);
    try {
      await productReviews.setReply(reviewId, reply);
      notify.success(reply ? "Ответ сохранён" : "Ответ удалён");
      onChanged();
      return true;
    } catch (err) {
      fail(err, "Не удалось сохранить ответ");
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** R8 — отзыв согласия на распространение ПДн; действие необратимо. */
  async function anonymize(): Promise<void> {
    if (!window.confirm("Обезличить автора? Имя будет заменено на «Покупатель».")) return;
    setBusy(true);
    try {
      await productReviews.anonymize(reviewId);
      notify.success("Автор обезличен");
      onChanged();
    } catch (err) {
      fail(err, "Не удалось обезличить автора");
    } finally {
      setBusy(false);
    }
  }

  /** Ленивая подгрузка audit-лога — только при первом раскрытии панели. */
  async function loadHistory(): Promise<void> {
    if (history !== null) return;
    try {
      setHistory(await productReviews.history(reviewId));
    } catch (err) {
      setHistory([]);
      fail(err, "Не удалось загрузить историю");
    }
  }

  return { busy, history, changeStatus, saveReply, anonymize, loadHistory };
}
